import { useState, useEffect } from 'react';
import { X, Copy, Share2, Check, Gift, Users, Snowflake } from 'lucide-react';
import { useApi } from '../hooks/useApi';

interface ReferralPromptProps {
  trigger: 'shift' | 'bingo' | 'course' | 'manual';
  courseName?: string;
  onClose: () => void;
}

interface ReferralCode {
  code: string;
  link: string;
  stats: {
    total: number;
    completed: number;
    pending: number;
  };
}

export default function ReferralPrompt({ trigger, courseName, onClose }: ReferralPromptProps) {
  const { fetchApi } = useApi();
  const [referralData, setReferralData] = useState<ReferralCode | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchReferralCode();
    // Trigger entrance animation
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  async function fetchReferralCode() {
    try {
      const data = await fetchApi<ReferralCode>('/api/referrals/code');
      setReferralData(data);
    } catch (error) {
      console.error('Failed to fetch referral code:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleCopyLink() {
    if (!referralData) return;

    try {
      await navigator.clipboard.writeText(referralData.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      // Track share
      fetchApi('/api/referrals/track-share', {
        method: 'POST',
        body: JSON.stringify({ shareMethod: 'copy' })
      }).catch(() => {});
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  }

  async function handleNativeShare() {
    if (!referralData || !navigator.share) return;

    try {
      await navigator.share({
        title: 'Join me on Northcoast AI Academy',
        text: 'Learn to think clearly and work effectively with AI. Use my invite code for bonus rewards!',
        url: referralData.link
      });

      // Track share
      fetchApi('/api/referrals/track-share', {
        method: 'POST',
        body: JSON.stringify({ shareMethod: 'native' })
      }).catch(() => {});
    } catch (error) {
      // User cancelled or share failed - that's ok
      if ((error as Error).name !== 'AbortError') {
        console.error('Share failed:', error);
      }
    }
  }

  function handleClose() {
    setIsVisible(false);
    setTimeout(onClose, 200);
  }

  const triggerMessages = {
    shift: 'You just discovered how AI can deceive!',
    bingo: 'You found your cognitive bias type!',
    course: `You completed ${courseName || 'a course'}!`,
    manual: 'Share the knowledge!'
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`relative bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 max-w-md w-full transform transition-all duration-200 ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-4">
              <Gift className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              {triggerMessages[trigger]}
            </h2>
            <p className="text-gray-400">
              Share with a friend and you both get rewards
            </p>
          </div>

          {/* Rewards breakdown */}
          <div className="bg-gray-800/50 rounded-xl p-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <p className="text-sm text-gray-400 mb-2">You get</p>
                <div className="flex items-center justify-center gap-2 text-blue-400">
                  <Users className="w-4 h-4" />
                  <span className="text-sm font-medium">Connector badge</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-cyan-400 mt-1">
                  <Snowflake className="w-4 h-4" />
                  <span className="text-sm font-medium">+3 freeze days</span>
                </div>
              </div>
              <div className="text-center border-l border-gray-700">
                <p className="text-sm text-gray-400 mb-2">They get</p>
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <Gift className="w-4 h-4" />
                  <span className="text-sm font-medium">Welcomed badge</span>
                </div>
                <div className="flex items-center justify-center gap-2 text-cyan-400 mt-1">
                  <Snowflake className="w-4 h-4" />
                  <span className="text-sm font-medium">+1 freeze day</span>
                </div>
              </div>
            </div>
          </div>

          {/* Referral code */}
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : referralData ? (
            <>
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Your invite code</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 bg-gray-800 rounded-lg px-4 py-3 font-mono text-lg text-white tracking-wider text-center border border-gray-700">
                    {referralData.code}
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className={`p-3 rounded-lg transition-all ${
                      copied
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700'
                    }`}
                    title="Copy invite link"
                  >
                    {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCopyLink}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>

                {typeof navigator !== 'undefined' && 'share' in navigator && (
                  <button
                    onClick={handleNativeShare}
                    className="flex-1 flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                )}
              </div>

              {/* Stats */}
              {referralData.stats.total > 0 && (
                <p className="text-center text-sm text-gray-500 mt-4">
                  You've invited {referralData.stats.total} friend{referralData.stats.total !== 1 ? 's' : ''}
                  {referralData.stats.completed > 0 && ` (${referralData.stats.completed} active)`}
                </p>
              )}
            </>
          ) : (
            <p className="text-center text-gray-400">
              Unable to load referral code. Please try again later.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 px-6 py-4">
          <button
            onClick={handleClose}
            className="w-full text-gray-400 hover:text-white text-sm transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
